import React, { useCallback } from 'react';
import { LayoutChangeEvent } from 'react-native';
import { Box } from '@styleseat/ui';
import { useOnce } from '../../../../hooks';
import analytics from '../../../../modules/analytics';
import {
  useCommissionPaymentState,
  useFraudBanner,
  useVerificationDetails,
  useVerificationErrorBanner,
} from './hooks';
import StripeVerificationBanner from '../../../provider/StripeVerificationBanner';
import FraudBanner from './FraudBanner';
import VerificationDetailsBanner from './VerificationDetailsBanner';
import MissingPaymentInfoBanner from './MissingPaymentInfoBanner';

export type AccountUpdateBannerContainerProps = {
  providerId: number;
  hideVerificationError?: boolean;
  onDismiss?: () => void;
  onBannerLayout?: (e: LayoutChangeEvent) => void;
};

const AccountUpdateBannerContainer: React.FC<AccountUpdateBannerContainerProps> = React.memo(
  ({
    providerId,
    hideVerificationError = false,
    onBannerLayout,
    onDismiss,
  }) => {
    const {
      editType,
      shouldShow: shouldShowFraudBanner,
      onDismiss: onFraudBannerDismiss,
    } = useFraudBanner(providerId);
    const handleFraudBannerDismiss = useCallback(() => {
      onFraudBannerDismiss();
      onDismiss?.();
    }, [onFraudBannerDismiss, onDismiss]);

    const {
      banner,
      shouldShow: shouldShowVerificationErrorBanner,
    } = useVerificationErrorBanner(
      !hideVerificationError && shouldShowFraudBanner === false,
    );
    const { identityVerification } = useVerificationDetails(providerId);

    const shouldShowVerificationDetailsBanner = !shouldShowFraudBanner
      && !shouldShowVerificationErrorBanner
      && (
        identityVerification?.needs_mailing_address_charges
        || identityVerification?.needs_verification_details
      );

    const {
      shouldShow: shouldShowPaymentBanner,
      planSelectionEnabled,
    } = useCommissionPaymentState(providerId);
    const shouldShowCommissionMissingPaymentInfoBanner = !shouldShowVerificationDetailsBanner
      && shouldShowPaymentBanner;

    const hasBanner = !!shouldShowFraudBanner
      || !!shouldShowVerificationErrorBanner
      || !!shouldShowVerificationDetailsBanner
      || !!shouldShowCommissionMissingPaymentInfoBanner;

    useOnce(() => {
      analytics.track('pro_account_update_banner_viewed');
      if (shouldShowFraudBanner) {
        analytics.track('P Saw Fraud Banner', { changed_info: editType });
      }
    }, hasBanner || null);

    if (!hasBanner) {
      return null;
    }

    return (
      <Box onLayout={onBannerLayout}>
        {shouldShowFraudBanner ? (
          <FraudBanner
            editType={editType}
            onDismiss={handleFraudBannerDismiss}
          />
        ) : null}
        {shouldShowVerificationErrorBanner ? (
          <Box
            padding={8}
            testID="stripeVerificationBanner"
          >
            <StripeVerificationBanner
              verificationIssueBanner={banner}
              providerId={providerId}
            />
          </Box>
        ) : null}
        {shouldShowVerificationDetailsBanner ? (
          <VerificationDetailsBanner identityVerification={identityVerification} />
        ) : null}
        {shouldShowCommissionMissingPaymentInfoBanner ? (
          <MissingPaymentInfoBanner
            providerId={providerId}
            planSelectionEnabled={planSelectionEnabled}
          />
        ) : null}
      </Box>
    );
  },
);

export default AccountUpdateBannerContainer;
